/**
 * Agents v3 — the agent settings card on `/agents/:agentType`.
 *
 * One thing a client understands: "how should this agent work for {brand}?" —
 * free text added on top of AdUp's base instructions (never replacing the
 * safety / whitelabel / output rules), plus a read-only "Data & tools" view of
 * the connected platforms. The model is chosen per agent type by AdUp (config,
 * not UI); document formats follow the agent's outputs; custom skills live on
 * the agency-level Skills tab. Saving re-provisions the config-hashed
 * Anthropic agent and re-points active deployments server-side; the card
 * re-pulls its own settings and asks the host page to re-pull the agents index.
 *
 * Two writes: **Save** (this brand only) and **Apply to all my brands** (the
 * agency-wide default, behind a confirm — it also clears this brand's own
 * override so the brand follows the default). Every edit is gated on
 * `portal.settings.manage` (`canManage`); reviewers see nothing (the GET is
 * manage-gated). A 422 lands inline next to the actions.
 *
 * The PUT deliberately sends ONLY `custom_instructions` (+ apply_to_agency):
 * an absent `prebuilt_skills` / `custom_skill_ids` means "inherit" server-side,
 * so a brand save never shadows the agency-level skill assignments.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import Popup from '../../../ui/popup/Popup';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  getAgentSettings,
  updateAgentSettings,
  type AgentSettingsView,
  type AgentTypeId,
} from '../../../lib/api/client-reports/client-agents-api';
import type { Translate } from '../../client-reports/agents/agent-helpers';
import '../../client-reports/client-reports.scss';

/** Which save is in flight — brand-only or agency-wide. */
type SavingScope = 'brand' | 'agency';

interface AgentSettingsCardProps {
  slug: string;
  agentType: AgentTypeId;
  /** `portal.settings.manage`; false → the card renders nothing. */
  canManage: boolean;
  /** The active brand's display name, for the card title. */
  brandName?: string;
  /**
   * The host page re-pulls its agents index after a save — a re-provision may
   * have re-pointed this brand's deployment at a new agent version.
   */
  onSettingsChanged?: () => void;
  /** Bumped by the host to force a re-pull of the settings. */
  refreshSignal?: number;
  /** Which sections to render. Default: everything. */
  sections?: AgentSettingsSection[];
}

export type AgentSettingsSection = 'instructions' | 'tools';
const ALL_SECTIONS: AgentSettingsSection[] = ['instructions', 'tools'];

/**
 * Example sentences a client can drop into the box — each appends on its own
 * line, so the copy stays theirs to edit.
 */
export const INSTRUCTION_EXAMPLES: string[] = [
  'Lead with blended ROAS, then break performance down by channel.',
  'Compare every metric with the KPI targets and say plainly whether we are on track.',
  'Write in Dutch, in a concise and factual tone.',
];

/** Friendly names for the platforms shown as read-only "Data & tools" chips. */
const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  meta: 'Meta',
  google_ads: 'Google Ads',
  google_analytics: 'Google Analytics',
  ga4: 'Google Analytics 4',
  google_search_console: 'Search Console',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
  microsoft_ads: 'Microsoft Ads',
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
};

function platformLabel(id: string): string {
  if (PLATFORM_LABEL[id]) return PLATFORM_LABEL[id];
  return String(id)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A PUT rejection → the inline sentence next to the actions. */
function friendlySettingsError(err: any, t: Translate): string {
  if (err?.status === 422) {
    const v = err.validation || {};
    const first = v.custom_instructions?.[0];
    if (first) return first;
    return (
      err.message ||
      t(
        'AgentSettingsRejected',
        'The instructions were rejected — check their length and try again.'
      )
    );
  }
  return (
    err?.message ||
    t('AgentSettingsSaveFailedRetry', 'Failed to save the settings — try again.')
  );
}

const AgentSettingsCard = ({
  slug,
  agentType,
  canManage,
  brandName,
  onSettingsChanged,
  refreshSignal,
  sections = ALL_SECTIONS,
}: AgentSettingsCardProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [view, setView] = useState<AgentSettingsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Draft state, seeded from `effective` on every (re-)load.
  const [instructions, setInstructions] = useState('');

  const [saving, setSaving] = useState<SavingScope | null>(null);
  /** Inline 422, rendered next to the actions that caused it. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmAgency, setConfirmAgency] = useState(false);

  const toast = useCallback(
    (message: string, messageType: 'success' | 'error' | 'info') =>
      dispatch(setMessage({ message, messageType })),
    [dispatch]
  );

  const seedDraft = useCallback((v: AgentSettingsView) => {
    setInstructions(v.effective?.custom_instructions ?? '');
  }, []);

  const load = useCallback(async () => {
    if (!slug || !agentType) return;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    try {
      const v = await getAgentSettings(slug, agentType);
      if (v && v.effective && v.options) {
        setView(v);
        seedDraft(v);
      } else {
        setView(null);
      }
    } catch (err: any) {
      setView(null);
      setLoadError(
        err?.message ||
          t('AgentSettingsLoadFailed', 'Failed to load the agent settings.')
      );
    } finally {
      setLoading(false);
    }
    // `t` is left out: a language switch must not re-fetch the settings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, agentType, seedDraft]);

  // Settings is a management surface; the GET is gated on
  // portal.settings.manage server-side, so only managers fetch it.
  useEffect(() => {
    if (canManage) load();
  }, [load, canManage, refreshSignal]);

  const connectedPlatforms = view?.connected_platforms ?? [];
  const instructionsMax = view?.instructions_max ?? 4000;
  const label = view?.label || t('AgentThisAgent', 'this agent');
  const brand = brandName || t('AgentThisBrand', 'this brand');
  const heading = t(
    'AgentSettingsHeading',
    'How should {{agent}} work for {{brand}}?',
    { agent: label, brand }
  );
  const examples = INSTRUCTION_EXAMPLES.map((sentence, i) =>
    t(`AgentSettingsExample${i + 1}`, sentence)
  );

  const sourceNote =
    view?.source === 'brand'
      ? t(
          'AgentSettingsSourceBrand',
          'These instructions are specific to {{brand}}.',
          { brand }
        )
      : view?.source === 'agency'
      ? t(
          'AgentSettingsSourceAgency',
          'Inherited from your agency default — saving here customises just {{brand}}.',
          { brand }
        )
      : view?.source === 'default'
      ? t(
          'AgentSettingsSourceDefault',
          "No instructions yet — {{agent}} runs on AdUp's defaults. Saving here customises just {{brand}}.",
          { agent: label, brand }
        )
      : '';

  const isDirty = useMemo(() => {
    if (!view) return false;
    return instructions !== (view.effective.custom_instructions ?? '');
  }, [view, instructions]);

  const appendExample = (sentence: string) => {
    setInstructions((prev) => {
      if (prev.includes(sentence)) return prev;
      const joined = prev.trim() === '' ? sentence : `${prev.trimEnd()}\n${sentence}`;
      return joined.slice(0, instructionsMax);
    });
  };

  const doSave = async (applyToAgency: boolean) => {
    if (!canManage || !view || saving) return;
    setSaveError(null);
    setSaving(applyToAgency ? 'agency' : 'brand');
    try {
      const result = await updateAgentSettings(slug, agentType, {
        custom_instructions: instructions,
        apply_to_agency: applyToAgency,
      });
      // Re-pull the settings (the agent may have re-provisioned); fall back to
      // the PUT's own fresh view if that re-pull blips.
      let fresh: AgentSettingsView | null = result.data ?? null;
      try {
        fresh = await getAgentSettings(slug, agentType);
      } catch {
        /* keep the PUT's returned view */
      }
      if (fresh && fresh.effective && fresh.options) {
        setView(fresh);
        seedDraft(fresh);
      }
      setConfirmAgency(false);
      onSettingsChanged?.();

      const done = applyToAgency
        ? t(
            'AgentSettingsSavedAgency',
            'Saved as your agency default and applied to this brand.'
          )
        : t('AgentSettingsSavedBrand', 'Saved for {{brand}}.', { brand });
      if (result.deployments_failed > 0) {
        toast(
          `${done} ${
            result.deployments_failed === 1
              ? t(
                  'AgentSettingsDeploymentFailedOne',
                  '{{failed}} active deployment could not be updated — AdUp has been notified.',
                  { failed: result.deployments_failed }
                )
              : t(
                  'AgentSettingsDeploymentFailedMany',
                  '{{failed}} active deployments could not be updated — AdUp has been notified.',
                  { failed: result.deployments_failed }
                )
          }`,
          'info'
        );
      } else {
        toast(done, 'success');
      }
    } catch (err: any) {
      setSaveError(friendlySettingsError(err, t));
      toast(
        err?.status === 422
          ? t(
              'AgentSettingsRejectedSeeForm',
              'The instructions were rejected — see the details on the form.'
            )
          : err?.message ||
              t('AgentSettingsSaveFailed', 'Failed to save the settings.'),
        'error'
      );
    } finally {
      setSaving(null);
    }
  };

  const overBudget = instructions.length >= instructionsMax;
  const show = (section: AgentSettingsSection) => sections.includes(section);

  // Reviewers can't manage settings and the GET is manage-gated — render
  // nothing rather than a permission error under the config page.
  if (!canManage) return null;

  const dataAndTools = (
    <div className='client-agents__settings-section'>
      <h4 className='client-agents__settings-title'>
        {t('AgentSettingsDataTools', 'Data & tools')}
      </h4>
      <p className='client-agents__settings-sub'>
        {t(
          'AgentSettingsDataToolsHint',
          "The connected platforms this brand's agent can read. Governed under Integrations (per-brand credentials + Tool Access), not here."
        )}
      </p>
      {connectedPlatforms.length > 0 ? (
        <div className='client-agents__tools'>
          {connectedPlatforms.map((p) => (
            <span key={p} className='client-agents__tool-chip'>
              {platformLabel(p)}
            </span>
          ))}
        </div>
      ) : (
        <p className='client-agents__tools-empty'>
          {t(
            'AgentSettingsNoPlatforms',
            'No platforms are connected for this brand yet.'
          )}
        </p>
      )}
      <Link className='client-agents__open-link' to='/integrations'>
        {t('AgentSettingsManageIntegrations', 'Manage integrations →')}
      </Link>
    </div>
  );

  return (
    <>
      <section className='client-agents__card client-agents__settings'>
        <div className='client-agents__card-head'>
          <div className='client-agents__card-id'>
            <h3 className='client-agents__card-title'>{heading}</h3>
          </div>
        </div>
        <p className='client-agents__card-hint'>
          {t(
            'AgentSettingsHint',
            "Tell it what to lead with, what to compare, and how to write. Your notes are added on top of AdUp's base instructions — the safety and whitelabel rules always apply."
          )}
        </p>

        {loading ? (
          <p className='client-agents__muted'>
            {t('AgentSettingsLoading', 'Loading settings…')}
          </p>
        ) : loadError ? (
          <div>
            <p className='client-agents__validation'>{loadError}</p>
            <div style={{ marginTop: '1rem' }}>
              <MainSquareButton
                type='button'
                value={t('Retry', 'Retry')}
                buttonType='light'
                normal
                onClick={load}
              />
            </div>
          </div>
        ) : !view ? (
          <p className='client-agents__muted'>
            {t(
              'AgentSettingsUnavailable',
              "These settings aren't available for this agent yet."
            )}
          </p>
        ) : (
          <>
            {sourceNote && (
              <p className='client-agents__source-note'>{sourceNote}</p>
            )}

            {/* ---------------- Instructions ---------------- */}
            {show('instructions') && (
              <div className='client-agents__settings-section'>
                <textarea
                  className='client-agents__textarea'
                  aria-label={heading}
                  value={instructions}
                  maxLength={instructionsMax}
                  disabled={saving !== null}
                  placeholder={t(
                    'AgentSettingsPlaceholder',
                    'e.g. Open with blended ROAS, then break performance down by channel. Keep the tone concise and factual.'
                  )}
                  onChange={(e) => setInstructions(e.target.value)}
                />
                <div
                  className={`client-agents__count${
                    overBudget ? ' client-agents__count--over' : ''
                  }`}
                >
                  {instructions.length} / {instructionsMax}
                </div>
                <div
                  className='client-agents__chips'
                  aria-label={t('AgentSettingsExamples', 'Examples')}
                >
                  <span className='client-agents__chips-label'>
                    {t('AgentWizardTry', 'Try:')}
                  </span>
                  {examples.map((sentence) => (
                    <button
                      key={sentence}
                      type='button'
                      className='client-agents__chip-btn'
                      disabled={saving !== null || instructions.includes(sentence)}
                      onClick={() => appendExample(sentence)}
                    >
                      {sentence}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ---------------- Data & tools ---------------- */}
            {show('tools') && dataAndTools}

            {/* ---------------- actions ---------------- */}
            <div className='client-agents__settings-actions'>
              {saveError && (
                <p className='client-agents__validation' role='alert'>
                  {saveError}
                </p>
              )}
              <div className='client-agents__settings-buttons'>
                <MainSquareButton
                  type='button'
                  value={
                    saving === 'brand'
                      ? t('AgentCardSaving', 'Saving…')
                      : t('Save', 'Save')
                  }
                  buttonType='success'
                  normal
                  loading={saving === 'brand'}
                  disabled={!isDirty || saving !== null}
                  message={
                    !isDirty ? t('AgentCardNoChanges', 'No changes to save.') : ''
                  }
                  onClick={() => doSave(false)}
                />
                <MainSquareButton
                  type='button'
                  value={
                    saving === 'agency'
                      ? t('AgentSettingsApplying', 'Applying…')
                      : t('AgentSettingsApplyAll', 'Apply to all my brands')
                  }
                  buttonType='light'
                  normal
                  loading={saving === 'agency'}
                  disabled={saving !== null}
                  onClick={() => setConfirmAgency(true)}
                />
              </div>
            </div>
          </>
        )}
      </section>

      {/* ---- apply-to-agency confirmation ---- */}
      {view && (
        <Popup
          trigger={confirmAgency}
          setTrigger={() => {
            if (saving !== 'agency') setConfirmAgency(false);
          }}
        >
          <div className='client-agents__confirm'>
            <h3 className='client-agents__confirm-title'>
              {t(
                'AgentSettingsConfirmTitle',
                'Apply these instructions to all your brands?'
              )}
            </h3>
            <p className='client-agents__confirm-body'>
              {t(
                'AgentSettingsConfirmBody',
                "They become your agency-wide default for {{agent}}. This brand's own instructions are cleared, so it follows the default too. Brands that already have their own instructions for this agent keep them.",
                { agent: label }
              )}
            </p>
            <div className='client-agents__confirm-actions'>
              <MainSquareButton
                type='button'
                value={t('Cancel', 'Cancel')}
                buttonType='light'
                normal
                disabled={saving === 'agency'}
                onClick={() => setConfirmAgency(false)}
              />
              <MainSquareButton
                type='button'
                value={
                  saving === 'agency'
                    ? t('AgentSettingsApplying', 'Applying…')
                    : t(
                        'AgentSettingsConfirmYes',
                        'Yes — apply to all brands'
                      )
                }
                buttonType='success'
                normal
                loading={saving === 'agency'}
                disabled={saving === 'agency'}
                onClick={() => doSave(true)}
              />
            </div>
          </div>
        </Popup>
      )}
    </>
  );
};

export default AgentSettingsCard;
